
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { Mail, ChevronRight, LineChart, BarChart, PieChart, Lock, Zap, Settings } from "lucide-react";
import { ParticlesBackground } from "@/components/ui/Particles";

const ComingSoon = () => {
  const [email, setEmail] = useState("");

  // Force dark mode for this page
  useEffect(() => {
    // Add dark class to html element
    document.documentElement.classList.add("dark");
    
    // Return cleanup function to restore theme when navigating away
    return () => {
      // Get the stored theme preference
      const storedTheme = localStorage.getItem("theme");
      if (storedTheme === "light") {
        document.documentElement.classList.remove("dark");
      }
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast({
        title: "Email is required",
        description: "Please enter your email address to subscribe",
        variant: "destructive",
      });
      return;
    }
    
    toast({
      title: "Thank you for subscribing!",
      description: "We'll notify you when we launch.",
    });
    setEmail("");
  };

  return (
    <div className="min-h-screen flex flex-col bg-background overflow-hidden relative">
      {/* Particles background - positioned with higher z-index but below content */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <ParticlesBackground fullScreen={true} />
      </div>
      
      <header className="w-full py-4 px-6 relative z-10">
        <div className="container mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src="/lovable-uploads/2f3c6a4e-d862-47d9-a9ec-3c5a97f55488.png" 
              alt="Bizlytics Logo" 
              className="h-12 w-auto" 
            />
            <span className="ml-2 text-xl font-bold">Bizlytics<span className="text-primary">.io</span></span>
          </Link>
          {/* Removed ThemeToggle */}
        </div>
      </header>

      <main className="flex-grow flex items-center justify-center relative z-10">
        <div className="container px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-3xl mx-auto text-center backdrop-blur-xl bg-black/40 border border-white/10 rounded-3xl p-10 shadow-2xl">
            <div className="relative mb-8 inline-block">
              <div className="absolute inset-0 rounded-full blur-3xl bg-yellow-300/20 dark:bg-yellow-500/20 -z-10"></div>
              <div className="backdrop-blur-md bg-white/10 dark:bg-slate-900/50 border border-white/20 dark:border-slate-700/30 rounded-xl p-4 shadow-xl">
                <img 
                  src="/lovable-uploads/fa909f5b-d62e-45ec-a325-8c5f97703d9f.png" 
                  alt="Bizlytics Logo" 
                  className="w-28 h-28 md:w-40 md:h-40 mx-auto rounded-xl" 
                />
              </div>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-yellow-500">Coming Soon</span>
            </h1>
            
            <p className="text-xl md:text-2xl mb-8 text-muted-foreground">
              Our intelligent business analytics platform is almost ready.
              We're working hard to bring you powerful insights and data visualization tools.
            </p>
            
            {/* Frosted glass card */}
            <div className="backdrop-blur-md bg-white/10 dark:bg-slate-900/30 border border-white/20 dark:border-slate-700/30 rounded-2xl shadow-xl p-8 mb-12 max-w-xl mx-auto transition-all duration-300 hover:shadow-2xl">
              <h2 className="text-2xl font-semibold mb-4">Get Notified When We Launch</h2>
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4">
                <div className="flex-grow">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 bg-white/10 border-white/20 placeholder:text-gray-400"
                  />
                </div>
                <Button type="submit" size="lg" className="button-shine">
                  <Mail className="mr-2 h-5 w-5" />
                  Subscribe
                </Button>
              </form>
            </div>

            {/* MVP Features */}
            <div className="mt-16 mb-8">
              <h2 className="text-3xl font-bold mb-8 text-white">
                <span className="relative inline-block">
                  <span className="absolute inset-0 transform translate-x-1 translate-y-1 bg-primary/20 rounded-lg blur-sm"></span>
                  <span className="relative">MVP Features</span>
                </span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
                <div className="backdrop-blur-md bg-white/5 border border-white/10 rounded-xl p-6 transition-all hover:bg-white/10 hover:scale-105 duration-300">
                  <div className="mb-4 p-3 bg-white/10 rounded-lg inline-block">
                    <LineChart className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Interactive Dashboards</h3>
                  <p className="text-gray-300 text-sm">Real-time data visualization with customizable widgets and filters</p>
                </div>
                
                <div className="backdrop-blur-md bg-white/5 border border-white/10 rounded-xl p-6 transition-all hover:bg-white/10 hover:scale-105 duration-300">
                  <div className="mb-4 p-3 bg-white/10 rounded-lg inline-block">
                    <BarChart className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Sales Analytics</h3>
                  <p className="text-gray-300 text-sm">Track revenue, conversions, and growth with detailed metrics and charts</p>
                </div>
                
                <div className="backdrop-blur-md bg-white/5 border border-white/10 rounded-xl p-6 transition-all hover:bg-white/10 hover:scale-105 duration-300">
                  <div className="mb-4 p-3 bg-white/10 rounded-lg inline-block">
                    <Lock className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Secure Data Storage</h3>
                  <p className="text-gray-300 text-sm">Enterprise-grade security with role-based access control and encryption</p>
                </div>
                
                <div className="backdrop-blur-md bg-white/5 border border-white/10 rounded-xl p-6 transition-all hover:bg-white/10 hover:scale-105 duration-300">
                  <div className="mb-4 p-3 bg-white/10 rounded-lg inline-block">
                    <Zap className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Data Integration</h3>
                  <p className="text-gray-300 text-sm">Connect to popular services including Stripe, QuickBooks, and Google Analytics</p>
                </div>
                
                <div className="backdrop-blur-md bg-white/5 border border-white/10 rounded-xl p-6 transition-all hover:bg-white/10 hover:scale-105 duration-300">
                  <div className="mb-4 p-3 bg-white/10 rounded-lg inline-block">
                    <PieChart className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Performance Metrics</h3>
                  <p className="text-gray-300 text-sm">Key business metrics and KPIs with historical comparison and forecasting</p>
                </div>
                
                <div className="backdrop-blur-md bg-white/5 border border-white/10 rounded-xl p-6 transition-all hover:bg-white/10 hover:scale-105 duration-300">
                  <div className="mb-4 p-3 bg-white/10 rounded-lg inline-block">
                    <Settings className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Custom Reports</h3>
                  <p className="text-gray-300 text-sm">Build and save custom reports with export options for team collaboration</p>
                </div>
              </div>
              
              {/* Removed the "Preview our landing page" link */}
            </div>
          </div>
        </div>
      </main>
      
      <footer className="py-6 border-t border-border relative z-10">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} <a href="https://domlytics.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary">Domlytics, LLC</a>. All rights reserved.</p>
        </div>
      </footer>
      
      {/* Background gradients with reduced opacity */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-br from-yellow-200/10 via-transparent to-transparent dark:from-yellow-700/5"></div>
        <div className="absolute bottom-0 right-0 w-full h-1/2 bg-gradient-to-tl from-blue-200/10 via-transparent to-transparent dark:from-blue-700/5"></div>
      </div>
    </div>
  );
};

export default ComingSoon;
